import {createSelector} from 'reselect';

import {PRODUCT_TYPES} from 'constants/product';
import {PRESCRIPTION_STATUS, VISTA_PLUS} from 'constants/success';
import {UPLOAD, SEND_LATER, FILL_IT} from 'types/successPage';

import {OrderedItemType} from '../types/orderItemsTypes';

import {getSuccessPageOrderDetailsItems} from '.';

/**
 * check that each prescription has equal status
 */
export const getIsAllCLPrescriptionsHasEqualStatus = (items: OrderedItemType[], status: number) =>
    Boolean(items.length) &&
    items
        .filter(item => item.product.type === PRODUCT_TYPES.CONTACT_LENSES)
        .every(item => item.prescription?.send_by === status);

export const getIsAllContacts = createSelector(getSuccessPageOrderDetailsItems, orderItems => {
    return orderItems.every(item => item.product.type === PRODUCT_TYPES.CONTACT_LENSES);
});

/**
 * checking that one of the frames in order skip prescription
 */
export const getIsFrameSkipPrescription = createSelector(
    getSuccessPageOrderDetailsItems,
    orderItems => {
        return Boolean(
            orderItems.find(
                item =>
                    item.product.type !== PRODUCT_TYPES.CONTACT_LENSES &&
                    item?.prescription?.send_by === SEND_LATER,
            ),
        );
    },
);

/**
 * checking when not all CL have same prescription
 */
export const getIsNotAllContactsHasSamePrescription = createSelector(
    getSuccessPageOrderDetailsItems,
    orderItems => {
        const filteredItems = orderItems.filter(
            item => item.product.type === PRODUCT_TYPES.CONTACT_LENSES,
        );

        if (!filteredItems.length) return false;

        const firstSendBy = filteredItems[0]?.prescription?.send_by;
        return !filteredItems.every(item => item?.prescription?.send_by === firstSendBy);
    },
);

/**
 * checking when all CL are vista and filled online
 */
export const getIsAllVistaFillOnline = (items: OrderedItemType[]) => {
    return (
        Boolean(items.length) &&
        items
            .filter(item => item.product.type === PRODUCT_TYPES.CONTACT_LENSES)
            .every(
                item =>
                    item.name.toLowerCase() === VISTA_PLUS.toLowerCase() &&
                    item.prescription.send_by !== UPLOAD,
            )
    );
};

/**
 * Computes the unified prescription status for contact lenses based on order items.
 * It checks if all contact lens prescriptions in an order share the same status,
 * returning a specific status or an empty string for mixed or undefined states.
 *
 * @param {Function} getSuccessPageOrderDetailsItems - Selector to extract order items from success page details.
 * @returns {string} The unified prescription status as one of `PRESCRIPTION_STATUS.UPLOAD`,
 *  `PRESCRIPTION_STATUS.FRAME_AND_CL_WITHOUT_PRESCRIPTION`, `PRESCRIPTION_STATUS.VISTA`,
 * `PRESCRIPTION_STATUS.SKIP`, `PRESCRIPTION_STATUS.FILL`, or an empty string for mixed/undefined statuses.
 */

export const getPrescriptionStatus = createSelector(
    getSuccessPageOrderDetailsItems,
    getIsNotAllContactsHasSamePrescription,
    getIsFrameSkipPrescription,
    (orderItems, isNotAllContactsHasSamePrescription, isFrameSkipPrescription) => {
        if (isNotAllContactsHasSamePrescription || isFrameSkipPrescription)
            return PRESCRIPTION_STATUS.MISSING_ITEM_PRESCRIPTION;
        if (getIsAllVistaFillOnline(orderItems)) return PRESCRIPTION_STATUS.VISTA;
        if (getIsAllCLPrescriptionsHasEqualStatus(orderItems, UPLOAD))
            return PRESCRIPTION_STATUS.UPLOAD;
        if (getIsAllCLPrescriptionsHasEqualStatus(orderItems, SEND_LATER))
            return PRESCRIPTION_STATUS.SKIP;
        if (getIsAllCLPrescriptionsHasEqualStatus(orderItems, FILL_IT))
            return PRESCRIPTION_STATUS.FILL;

        return '';
    },
);
